import { useCallback, useEffect, useMemo, useState } from 'react';

import { useWindowSize } from './useWindowSize';

import {
  getContainerStyle,
  getSlideSize,
  getSlidesData,
  getPositionsMapping,
  getPosition,
  getSlideStyle,
  getDisplayArrow
} from '@/utils/carouselUtils';

// the required distance between touchStart and touchEnd to be detected as a swipe
const MIN_SWIPE_DISTANCE = 50;

export const useSlider = ({ children, perPage, gap, cropped, ref, direction, indexSelected, slideBy, carouselKey }) => {
  const [withTransform, setWithTransform] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hover, setHover] = useState(false);
  const [index, setIndex] = useState(indexSelected);
  const size = useWindowSize();

  const effectivePerPage = Math.min(children.length, perPage);
  const effectiveCropped = cropped && effectivePerPage !== 1;
  const client = direction === 'horizontal' ? 'clientX' : 'clientY';

  const onMouseEnter = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    setHover(false);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0][client]);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0][client]);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isPreviousSwipe = distance > MIN_SWIPE_DISTANCE;
    const isNextSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isPreviousSwipe) {
      translate('next');
    }
    if (isNextSwipe) {
      translate('previous');
    }
  };

  useEffect(() => {
    /** no transform css animation when
       * size, perPage, gap or cropped change */
    setWithTransform(false);

    setContainerWidth(ref.container.current.offsetWidth);

    /**
     * We used child ref to compute height because
     * container has no height at the init
     */
    if (direction !== 'horizontal') {
      setContainerHeight(
        ref.child.current?.offsetHeight * (effectivePerPage - (effectiveCropped ? 0.5 : 0)) + (effectivePerPage - 1) * gap
      );
    }
  }, [
    size, effectivePerPage, gap, effectiveCropped, ref.container.current?.offsetWidth, ref.child.current?.offsetHeight, direction
  ]);

  useEffect(() => {
    setIndex(indexSelected);
  }, [effectivePerPage, gap, effectiveCropped, direction, indexSelected, slideBy, carouselKey]);

  const containerStyle = useMemo(() => (
    getContainerStyle(gap, direction, containerHeight)
  ), [gap, direction, containerHeight]);

  const slideSize = useMemo(() => (
    getSlideSize(direction === 'horizontal' ? containerWidth : containerHeight, perPage, gap, effectiveCropped)
  ), [containerWidth, containerHeight, perPage, gap, effectiveCropped]);

  const slidesData = useMemo(() => (
    getSlidesData(children, slideSize, gap)
  ), [children, slideSize, gap]);

  const positionsMapping = useMemo(() => (
    getPositionsMapping(children, slidesData, direction === 'horizontal' ? containerWidth : containerHeight, effectiveCropped)
  ), [children, slidesData, containerWidth, containerHeight, effectiveCropped]);

  const position = useMemo(() => (
    getPosition(positionsMapping, effectivePerPage, index)
  ), [positionsMapping, effectivePerPage, index]);

  const slideStyle = useMemo(() => (
    getSlideStyle(position, withTransform, slideSize, direction)
  ), [position, withTransform, slideSize, direction]);

  const displayArrow = useMemo(() => (
    getDisplayArrow(hover, index, positionsMapping, effectivePerPage, perPage, cropped)
  ), [hover, index, positionsMapping, effectivePerPage, perPage, cropped]);

  const translate = useCallback((action) => {
    setWithTransform(true);

    const positions = positionsMapping[effectivePerPage];
    const minIndex = 0;
    const maxIndex = positions.length - 1;

    if (action === 'next') {
      setIndex(((index + slideBy) > maxIndex) ? maxIndex : index + slideBy);
    }
    if (action === 'previous') {
      setIndex(((index - slideBy) < minIndex) ? minIndex : index - slideBy);
    }
  }, [index, slideBy, positionsMapping, effectivePerPage]);

  return {
    index,
    containerStyle,
    slideStyle,
    slideSize,
    displayArrow,
    onMouseEnter,
    onMouseLeave,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    translate
  };
};
